import { createFileRoute } from "@tanstack/react-router";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import {
  OutputTrendChart,
  monthOutput,
} from "@/components/submissions/OutputTrendChart";
import { useAuth } from "@/lib/auth-context";
import { getDepartmentConfig } from "@/lib/department-configs";
import { STATUS_LABELS, SUBMISSION_STATUSES } from "@/lib/submissions";

export const Route = createFileRoute("/_app/performance")({
  component: PerformancePage,
});

function PerformancePage() {
  const { profile } = useAuth();
  const { data: rows = [] } = useMySubmissions();
  if (!profile) return null;
  const config = getDepartmentConfig(profile.department);
  const isEditor = profile.department === "editor";

  const month = monthOutput(rows);
  const totalPoints = month.reduce((s, r) => s + r.points, 0);
  const avgPerDay = month.length
    ? (month.length / new Date().getDate()).toFixed(1)
    : "0";

  // Group by first selectable detail field (e.g. video_type, design_type, channel)
  const groupField = config.detailFields.find((f) => f.type === "select");
  const byGroup = new Map<string, number>();
  if (groupField) {
    for (const r of month) {
      const v = String((r.details as Record<string, unknown>)[groupField.key] ?? "—");
      byGroup.set(v, (byGroup.get(v) ?? 0) + 1);
    }
  }

  const byStatus = new Map<string, number>();
  for (const r of month) byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);

  return (
    <>
      <AppHeader subtitle="Your performance breakdown for this month" />
      <KpiRow
        items={[
          {
            label: config.kpis.outputLabel,
            value: String(month.length),
            trend: `${avgPerDay}/day avg`,
            variant: "primary",
          },
          ...(isEditor
            ? [{ label: "Points Earned", value: String(totalPoints), trend: "this month" }]
            : []),
          {
            label: "Correction Rate",
            value: month.length
              ? `${Math.round(((byStatus.get("correction_needed") ?? 0) / month.length) * 100)}%`
              : "—",
            trend: `${byStatus.get("correction_needed") ?? 0} flagged`,
            variant: "danger",
          },
          {
            label: "Approval Rate",
            value: month.length
              ? `${Math.round(((byStatus.get("approved") ?? 0) / month.length) * 100)}%`
              : "—",
            trend: `${byStatus.get("approved") ?? 0} approved`,
          },
          { label: "Streak", value: "—", trend: "Coming soon" },
        ]}
      />
      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <OutputTrendChart rows={rows} noun={config.itemNoun} />
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Breakdown
          </div>
          <h2 className="text-[18px] font-bold">By status & {groupField?.label.toLowerCase() ?? "type"}</h2>

          <div className="mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
              Status
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {SUBMISSION_STATUSES.map((s) => {
                const v = byStatus.get(s) ?? 0;
                const pct = month.length ? (v / month.length) * 100 : 0;
                return (
                  <div key={s}>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>{STATUS_LABELS[s]}</span>
                      <span className="text-muted-foreground">{v}</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {groupField && (
            <div className="mt-5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                {groupField.label}
              </div>
              <div className="mt-2 flex flex-col gap-2">
                {[...byGroup.entries()].length === 0 && (
                  <p className="text-[11px] text-muted-foreground">No data yet.</p>
                )}
                {[...byGroup.entries()].map(([t, v]) => {
                  const pct = month.length ? (v / month.length) * 100 : 0;
                  return (
                    <div key={t}>
                      <div className="flex items-center justify-between text-[11px]">
                        <span>{t}</span>
                        <span className="text-muted-foreground">{v}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-foreground" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
